import React, { useState } from 'react';
import { WarehouseNode, Product, DemandData } from '../../types';
import './DataGrids.css';

interface DemandGridProps {
  nodes: WarehouseNode[];
  products: Product[];
  demand: DemandData[];
  onDemandUpdate: (nodeId: string, productId: string, quantity: number) => Promise<void>;
}

const DemandGrid: React.FC<DemandGridProps> = ({
  nodes,
  products,
  demand,
  onDemandUpdate
}) => {
  const [editingCell, setEditingCell] = useState<string | null>(null);
  const [tempValues, setTempValues] = useState<{ [key: string]: string }>({});
  const [updating, setUpdating] = useState<{ [key: string]: boolean }>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const getDemandQuantity = (nodeId: string, productId: string): number => {
    const demandItem = demand.find(
      dem => dem.node_id === nodeId && dem.product_id === productId
    );
    return demandItem?.quantity || 0;
  };

  const getCellKey = (nodeId: string, productId: string): string => {
    return `${nodeId}-${productId}`;
  };

  const handleCellClick = (nodeId: string, productId: string) => {
    const cellKey = getCellKey(nodeId, productId);
    setEditingCell(cellKey);
    setTempValues({
      ...tempValues,
      [cellKey]: getDemandQuantity(nodeId, productId).toString()
    });
    setErrors({
      ...errors,
      [cellKey]: ''
    });
  };

  const handleInputChange = (cellKey: string, value: string) => {
    setTempValues({
      ...tempValues,
      [cellKey]: value
    });

    // Real-time validation
    const numValue = parseFloat(value);
    if (value === '' || isNaN(numValue) || numValue < 0) {
      setErrors({
        ...errors,
        [cellKey]: value === '' ? '' : 'Demand must be a non-negative number'
      });
    } else {
      setErrors({
        ...errors,
        [cellKey]: ''
      });
    }
  };

  const handleCellSubmit = async (nodeId: string, productId: string) => {
    const cellKey = getCellKey(nodeId, productId);
    const value = tempValues[cellKey];
    
    if (!value || value.trim() === '') {
      setEditingCell(null);
      return;
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue < 0) {
      setErrors({
        ...errors,
        [cellKey]: 'Demand must be a non-negative number'
      });
      return;
    }

    try {
      setUpdating({
        ...updating,
        [cellKey]: true
      });

      await onDemandUpdate(nodeId, productId, numValue);
      setEditingCell(null);
      setErrors({
        ...errors,
        [cellKey]: ''
      });
    } catch (error) {
      setErrors({
        ...errors,
        [cellKey]: 'Failed to update demand'
      });
    } finally {
      setUpdating({
        ...updating,
        [cellKey]: false
      });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, nodeId: string, productId: string) => {
    if (e.key === 'Enter') {
      handleCellSubmit(nodeId, productId);
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      const cellKey = getCellKey(nodeId, productId);
      setErrors({
        ...errors,
        [cellKey]: ''
      });
    }
  };

  const handleBlur = (nodeId: string, productId: string) => {
    // Small delay to allow click events to register
    setTimeout(() => {
      handleCellSubmit(nodeId, productId);
    }, 100);
  };

  return (
    <div className="demand-grid">
      <div className="grid-header">
        <h3>Customer Demand Forecast</h3>
        <p>Click on any cell to edit demand quantities. Values must be non-negative numbers.</p>
      </div>

      <div className="grid-container">
        <table className="data-grid">
          <thead>
            <tr>
              <th className="node-header">Warehouse Node</th>
              {products.map(product => (
                <th key={product.product_id} className="product-header">
                  {product.name}
                  <span className="unit">({product.unit_of_measure})</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {nodes.map(node => (
              <tr key={node.node_id}>
                <td className="node-cell">
                  <div className="node-info">
                    <strong>{node.name}</strong>
                    <span className="location">{node.location}</span>
                  </div>
                </td>
                {products.map(product => {
                  const cellKey = getCellKey(node.node_id, product.product_id);
                  const isEditing = editingCell === cellKey;
                  const isUpdating = updating[cellKey];
                  const hasError = errors[cellKey];
                  const quantity = getDemandQuantity(node.node_id, product.product_id);

                  return (
                    <td 
                      key={cellKey} 
                      className={`quantity-cell ${isEditing ? 'editing' : ''} ${hasError ? 'error' : ''}`}
                    >
                      {isEditing ? (
                        <div className="input-container">
                          <input
                            type="number"
                            min="0"
                            step="1"
                            value={tempValues[cellKey] || ''}
                            onChange={(e) => handleInputChange(cellKey, e.target.value)}
                            onKeyDown={(e) => handleKeyPress(e, node.node_id, product.product_id)}
                            onBlur={() => handleBlur(node.node_id, product.product_id)}
                            className="quantity-input"
                            autoFocus
                            disabled={isUpdating}
                          />
                          {hasError && (
                            <div className="error-message">{hasError}</div>
                          )}
                        </div>
                      ) : (
                        <div 
                          className="quantity-display"
                          onClick={() => handleCellClick(node.node_id, product.product_id)}
                        >
                          {isUpdating ? (
                            <div className="updating">
                              <span className="spinner-small"></span>
                              Updating...
                            </div>
                          ) : (
                            <span className="quantity-value">{quantity}</span>
                          )}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid-footer">
        <div className="summary">
          <strong>Total Demand:</strong> {
            demand
              .filter(dem => 
                nodes.some(node => node.node_id === dem.node_id) &&
                products.some(product => product.product_id === dem.product_id)
              )
              .reduce((sum, dem) => sum + dem.quantity, 0)
          } units across {nodes.length} nodes
        </div>
        <div className="instructions">
          <small>
            💡 Tip: Click any cell to edit, press Enter to save, or Escape to cancel
          </small>
        </div>
      </div>
    </div>
  );
};

export default DemandGrid;